import type { PbjPrediction } from "../../../utils/models";
import { effectiveBaselineForPrefix, planPointsScale, scoreGapFromBaseline, WEEKEND_CATEGORY_ID } from "./cumulativeScoring.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { PlanItem, PlanItemCategoryBreakdown, RecommendationType } from "./recommendations.constants";

// FE mirror of the backend greedy planner's HPPD→points→hours math
// (`backend/services/pbj_model/greedy.py`). Pure and React-free so the sidebar's
// render path, its slider-drag re-score, and its "Add all" all share one implementation
// (this piece is as parity-critical as its already-extracted siblings
// `cumulativeScoring.utils` and `planOutcome.utils`).

// `points` are TOTAL points (already through `planPointsScale`), so they add up with `totalPoints`.
type RecBucketBreakdown = { categoryId: string; categoryTitle: string; points: number; hppdGap: number };

export type RecData = {
	// Headline HPPD impact — the *primary category's* SIGNED adjusted-HPPD change
	// (matches `spend = primary_gap × wage`; negative on a cut). Coupled non-primary categories see a smaller
	// bump (see `breakdown[i].hppdGap`) when their denominator differs (weekend
	// recs against Total/RN, which span all days).
	impact: number;
	// Signed: > 0 points gained by an add, < 0 points lost by a cut crossing a band floor.
	points: number;
	// Signed whole hours per applicable day (adds round up, cuts toward zero).
	hours: number;
	// Signed catch-up rate (per remaining applicable day). null when no applicable days
	// remain, or for a cut past the lever's per-day cut limit. See `catchupHours`.
	hoursCatchup: number | null;
	// The spend this change is PRICED at — see `pricedSpendFor`.
	pricedSpend: number;
	// One entry per HPPD category the recommendation affects, with the projected
	// point gain AND the per-category HPPD bump (which may differ between cats —
	// e.g. weekend recs bump Weekend HPPD by `primary_gap` but Total HPPD by
	// only `primary_gap × weekendDays / quarterDays` because the hours only
	// land on weekend shifts). Used by the "+X pts" tooltip + the per-cat HPPD
	// labels on the rec card.
	breakdown: RecBucketBreakdown[];
};

// Mirror of `MAX_HPPD_PER_RECOMMENDATION` in `backend/services/pbj_model/constants.py`.
// Caps each rec's primary-category HPPD bump so the slider/projection can't claim
// gains beyond what the BE greedy is willing to suggest. MUST stay in lockstep with
// the BE constant: 3.0 covers the widest possible single band crossing (Total HPPD
// bottom edge 2.722), so the greedy never rejects a facility's first crossing.
// Bounds ADDS only — a cut's floor is the backend-supplied per-lever cut limit.
export const MAX_HPPD_PER_RECOMMENDATION = 3.0;

// Frontend-only guard for signed manual plans. A cut under one hour per applicable day
// rounds to 0 hours toward zero, so the Action Plan does not keep a zero-hour cut.
export const MIN_CUT_HOURS_PER_DAY = 1;

// Mirror of `MAX_CUT_SHARE_PER_LEVER` in `backend/services/pbj_model/constants.py` — the share of a
// role's current hours one lever may cut. The backend applies it (it ships each lever's cut limit
// already in hours); the FE only names it in the cut-limit note, so the copy and the cap can't drift.
export const MAX_CUT_SHARE_PER_LEVER = 0.25;

// Absorbs float error before flooring a cut's magnitude so e.g. 2.9999999999 h (a cut
// that was scored at exactly 3 h) still rounds to 3, not 2. Same value as the backend
// `_to_steps` cut rounding, so both sides commit the identical whole-hour cut.
const CUT_ROUNDING_EPSILON = 1e-9;

// Whole-hour rounding for a signed per-day rate, mirroring the backend `_to_steps`:
// adds round UP (never schedule less than was scored), cuts round TOWARD ZERO (never
// cut more than was scored — the points figure was computed for the exact cut, and a
// bigger cut could cross a band floor the user was told it wouldn't).
export const roundStepHours = (hours: number): number => {
	if (hours > 0) return Math.ceil(hours);
	const magnitude = Math.floor(Math.abs(hours) + CUT_ROUNDING_EPSILON);
	// Returning `-0` would make `hours === 0` checks pass but `Object.is`/display differ.
	return magnitude === 0 ? 0 : -magnitude;
};

// CMI scale of the rec's primary category — what turns reported HPPD into adjusted HPPD.
export const primaryScaleFor = (rec: RecommendationType, prediction: PbjPrediction): number => {
	const primaryCat = prediction.categories.find((c) => c.id === rec.primaryCategoryId);
	// DEFAULT: neutral scale (1) when the rec's primary category isn't in this prediction
	// (e.g. measure flagged N/A) — the rec then moves no HPPD that matters, so any scale
	// gives the same 0-point result; 1 keeps the hours readout un-adjusted.
	return primaryCat?.scale ?? 1;
};

// Deepest cut this lever allows, as a (positive) adjusted-HPPD magnitude on its primary
// category. The backend ships the limit in hours per applicable day — the same unit as
// a step's `hours` — so this inverts `hours = primaryGap × census / primaryScale`.
const maxCutGapFor = (rec: RecommendationType, prediction: PbjPrediction, census: number): number => {
	// A lever missing from `cutLimits` legitimately means "can't cut" (a quarter that hasn't
	// started, or no worked hours for the role) — identical to a 0-hour limit, not a fallback.
	const limit = prediction.cutLimits[rec.id];
	if (limit === undefined || census <= 0) return 0;
	return (limit.maxCutHoursPerDay * primaryScaleFor(rec, prediction)) / census;
};

// Slider's negative bound, as a positive magnitude in the slider's spend units
// (`spend = primaryGap × wage`, $/applicable-PPD). The slider's minimum is `-maxCutSpend`.
// 0 when the lever can't be cut (or has no wage, so no dollar axis to slide along).
// Pass the SAME `census` given to `calculateRecData` so the slider floor and its clamp agree.
export const maxCutSpend = (rec: RecommendationType, prediction: PbjPrediction, wage: number, census: number): number => {
	if (wage <= 0) return 0;
	return maxCutGapFor(rec, prediction, census) * wage;
};

// The spend a change is PRICED at ("$X total" / "Saves $X"), in the same $/applicable-PPD units as
// `spend`. Adds price at their exact `spend`, unchanged. A cut prices at the whole-hour cut actually
// committed: its hours round TOWARD ZERO (`roundStepHours`), so pricing the exact spend would claim
// savings for up to an hour a day that is never cut — at −1.99 h/day the card would read "Cut 7 hours
// per week" (1 h/day) beside the savings of 2 h/day. `hours × wage × primaryScale / census` is the
// spend of exactly `hours` per day (inverting `hours = spend / wage × census / primaryScale`), so the
// money always matches the hours on screen for manual slider cuts.
export const pricedSpendFor = (spend: number, roundedHours: number, wage: number, primaryScale: number, census: number): number => {
	if (spend >= 0) return spend;
	if (census <= 0 || roundedHours === 0) return 0;
	return (roundedHours * wage * primaryScale) / census;
};

// `pricedSpendFor` for a backend step. Automatic suggestions are additions-only; the signed branch
// remains for schema compatibility with older responses.
export const stepPricedSpend = (rec: RecommendationType, step: { spend: number; hours: number }, prediction: PbjPrediction): number => {
	if (step.spend >= 0) return step.spend;
	const wage = prediction.deptWages[rec.wageKey];
	if (wage === null) throw new Error(`Cut step on ${rec.id} has no wage`);
	return pricedSpendFor(step.spend, step.hours, wage, primaryScaleFor(rec, prediction), prediction.resolvedAverageCensus);
};

// Compute the projected SIGNED point change for a recommendation, mirroring the BE
// greedy's per-category delta math (`backend/services/pbj_model/greedy.py`). Positive
// spend adds hours, negative spend cuts them; every figure in the result carries that
// sign. Key details:
//
//   - `primary_gap = spend / wage` is the adjusted-HPPD change on the rec's *primary*
//     category (also the headline `±X.XX HPPD` shown on the card), clamped to
//     `[−maxCutGap, MAX_HPPD_PER_RECOMMENDATION]` — the cut floor is the backend's
//     per-lever cut limit (0 when the lever can't be cut).
//   - `hoursTotal = primary_gap × census × days(primary) / primary_scale` translates
//     that into actual reported staffing hours across the applicable day window
//     (all days for weekday recs, weekend days for weekend recs). The `/ primary_scale`
//     undoes the CMI adjustment so we're working in reported-hours, not adjusted-HPPD.
//   - Each coupled category sees `shift = hoursTotal / (census × days(cat)) × scale(cat)`
//     i.e. the same hours spread over THAT category's denominator. For weekend recs
//     this means Total/RN HPPD see a smaller change than Weekend HPPD (hours only
//     land on weekend shifts).
//   - The user-facing `hours` (per-day sustained rate) is `hoursTotal / days(primary)`
//     = `primary_gap × census / primary_scale`, also CMI-reversed, rounded like the BE
//     (`roundStepHours`: adds up, cuts toward zero).
//   - Band scoring runs against the EFFECTIVE (buffered) ladder — the one `totalPoints` and
//     the backend optimizer use (see `pointsForHppd`) — and the raw measure points go
//     through `planPointsScale` so they're in total points when a measure is N/A. A cut that
//     stays inside its band scores 0; one that crosses a band floor scores the points lost.
export const calculateRecData = (rec: RecommendationType, spend: number, wage: number, prediction: PbjPrediction, census: number): RecData => {
	const unclampedGap = wage > 0 ? spend / wage : 0;
	// `0 - x` rather than `-x`: a 0 limit must give +0, or a non-cuttable lever dragged left
	// would report an impact of -0.
	const cutFloor = 0 - maxCutGapFor(rec, prediction, census);
	const primaryGap = Math.max(cutFloor, Math.min(unclampedGap, MAX_HPPD_PER_RECOMMENDATION));

	// Days denominator depends on whether the rec lands on weekends only.
	const recDays = rec.isWeekend ? prediction.weekendDays : prediction.days;
	// Hours of this action that produce `primaryGap` on its primary category. We use
	// the primary cat's denominator + scale to back out hours from the HPPD change,
	// then re-distribute those same hours across every coupled category.
	const primaryScale = primaryScaleFor(rec, prediction);
	const hoursTotal = census > 0 && recDays > 0 && primaryScale > 0 ? (primaryGap * census * recDays) / primaryScale : 0;
	const breakdown = categoryBreakdown(rec, prediction, hoursTotal, census);
	const points = breakdown.reduce((sum, b) => sum + b.points, 0);
	// Reverse the CMI adjustment so `hours` is denominated in actual reported
	// staffing hours, not adjusted-HPPD-times-census. Mirrors the BE greedy's
	// `hppd_gap_reported = primary_gap / primary_scale` step.
	const primaryGapReported = primaryScale > 0 ? primaryGap / primaryScale : 0;
	const hours = roundStepHours(primaryGapReported * census);
	return {
		impact: primaryGap,
		points,
		hours,
		hoursCatchup: catchupHours(rec, prediction, primaryGapReported * census),
		pricedSpend: pricedSpendFor(spend, hours, wage, primaryScale, census),
		breakdown,
	};
};

// The same `hoursTotal` spread over each coupled category's own denominator, scored on its band ladder:
// `shift = hoursTotal / (census × days(cat)) × scale(cat)`. Weekend HPPD always uses weekend days,
// Total/RN HPPD always use all days — the recDays-vs-catDays mismatch is what gives weekend recs the
// smaller cross-category change. Points come back in total-point units (`planPointsScale`).
const categoryBreakdown = (rec: RecommendationType, prediction: PbjPrediction, hoursTotal: number, census: number): RecBucketBreakdown[] => {
	const pointsScale = planPointsScale(prediction);
	return rec.impactedIds.flatMap((id): RecBucketBreakdown[] => {
		const cat = prediction.categories.find((c) => c.id === id);
		if (!cat) return [];
		const catDays = cat.id === WEEKEND_CATEGORY_ID ? prediction.weekendDays : prediction.days;
		const shift = census > 0 && catDays > 0 ? (hoursTotal / (census * catDays)) * cat.scale : 0;
		// scoreGapFromBaseline nudges before scoring (BE `_to_steps` parity) so a
		// BE-suggested spend whose shift lands a hair below its band cutoff still
		// credits the crossing here, matching the Action Plan row's cumulative pass.
		// For a cut the same nudge errs toward fewer points lost, as the BE does.
		const { gain } = scoreGapFromBaseline(cat, cat.rawValue, shift);
		return [{ categoryId: id, categoryTitle: cat.title, points: gain * pointsScale, hppdGap: shift }];
	});
};

// Catch-up: same headline HPPD change spread over the remaining-applicable-day window
// only. Weekend recs use weekend day counts (hours land on weekend shifts only); weekday
// recs use calendar day counts. null when no applicable days remain, or — for a cut —
// when the catch-up rate would cut past the lever's per-day limit (infeasible: the
// remaining days can't absorb that much of a cut). Rounded like `hours`.
const catchupHours = (rec: RecommendationType, prediction: PbjPrediction, sustainedHoursPerDay: number): number | null => {
	const fullDays = rec.isWeekend ? prediction.quarterWeekendDays : prediction.quarterDays;
	const remainingDays = rec.isWeekend ? prediction.weekendDaysRemaining : prediction.daysRemaining;
	if (remainingDays <= 0 || fullDays <= 0) return null;
	const catchup = roundStepHours((sustainedHoursPerDay * fullDays) / remainingDays);
	if (catchup >= 0) return catchup;
	// Missing limit = lever can't be cut at all (see `maxCutGapFor`), so any cut is over it.
	const limit = prediction.cutLimits[rec.id];
	if (limit === undefined || Math.abs(catchup) > limit.maxCutHoursPerDay) return null;
	return catchup;
};

// Normalize the slider's raw $/applicable-PPD value into the per-calendar-PPD rate
// shown on the rec card. The effective window depends on the period toggle:
//   - "quarter" mode → spread across the full quarter (sustained rate)
//   - "qtd" mode     → spread across only the remaining catch-up days (higher rate)
// Sum of these displayed values across all selected recs equals the Action Plan's
// "Total PPD Spend" line in the same mode.
export const displaySpendForLabel = (rec: RecommendationType, rawSpend: number, prediction: PbjPrediction, periodMode: PeriodMode): number => {
	const applicableQuarterDays = rec.isWeekend ? prediction.quarterWeekendDays : prediction.quarterDays;
	const effDays = periodMode === "qtd" && prediction.daysRemaining > 0 ? prediction.daysRemaining : prediction.quarterDays;
	if (effDays <= 0 || prediction.quarterDays <= 0) return rawSpend;
	return (rawSpend * applicableQuarterDays) / effDays;
};

// `data.breakdown` carries each touched category's per-category `hppdGap` (the
// baseline-INDEPENDENT shift — it depends only on hours/census/days/scale, not on
// what else is in the plan) plus a standalone-scored point gain. We attach the
// breakdown verbatim to every plan item — including MANUAL slider/drag items — so
// the cumulative pass in ActionItems can re-score each item's marginal points
// against the running prefix. The `points`/`hppdGap` on each bucket are stored in
// the `PlanItemCategoryBreakdown` shape (identical to BE-suggested items), and the
// cumulative pass overrides the `points` at render time; only `hppdGap` is
// load-bearing here and it is order-independent.
export const buildPlanItem = (rec: RecommendationType, spend: number, data: RecData): PlanItem => {
	const breakdown: PlanItemCategoryBreakdown[] = data.breakdown.map((b) => ({
		categoryId: b.categoryId,
		categoryTitle: b.categoryTitle,
		hppdGap: b.hppdGap,
		points: b.points,
	}));
	return {
		id: rec.id,
		titleKey: rec.titleKey,
		spend,
		pricedSpend: data.pricedSpend,
		impact: data.impact,
		points: data.points,
		hours: data.hours,
		hoursCatchup: data.hoursCatchup,
		actionKey: rec.actionKey,
		actionCatchupKey: rec.actionCatchupKey,
		breakdown,
	};
};

// A lever's per-category gaps re-scored as MARGINAL points over the plan prefix before it — the FE
// analog of the backend greedy's add-order attribution (`_to_steps`), so a rec card's "±X pts" equals
// its Action Plan row. The baseline is `rawValue` + the shift of the OTHER plan items added before this
// rec: the items before it when it's in the plan (`isSelected`), the whole plan otherwise. Each touched
// category advances the local running value to the nudged point it was scored at
// (`scoreGapFromBaseline`), so two categories of one rec never double-credit a shared edge. Points come
// back in total-point units (`planPointsScale`). Pure, so the row's render path stays thin and testable.
export const rescoreRecAgainstPlan = (
	rec: RecommendationType,
	gapBreakdown: ReadonlyArray<RecBucketBreakdown>,
	selectedPlan: PlanItem[],
	prediction: PbjPrediction,
	isSelected: boolean,
): { points: number; breakdown: RecBucketBreakdown[] } => {
	const running = effectiveBaselineForPrefix(selectedPlan, prediction.categories, isSelected ? rec.id : undefined);
	const pointsScale = planPointsScale(prediction);
	const breakdown = gapBreakdown.map((b) => {
		const cat = prediction.categories.find((c) => c.id === b.categoryId);
		if (!cat) return { ...b, points: 0 };
		// DEFAULT: a category the plan prefix never touched isn't in `running` only when it has no band
		// ladder (turnover/admin) — its own raw value is then the honest baseline.
		const before = running[b.categoryId] ?? cat.rawValue;
		const { gain, after } = scoreGapFromBaseline(cat, before, b.hppdGap);
		running[b.categoryId] = after;
		return { ...b, points: gain * pointsScale };
	});
	return { points: breakdown.reduce((sum, b) => sum + b.points, 0), breakdown };
};
